// <s-category-loop>: endless horizontal category row. The items are cloned into extra sets so the shopper can
// keep scrolling (or tapping the arrows) in either direction; when the scroll drifts out of the middle set it
// silently jumps back by one set width, so the row never reaches an end.
const SETS = 5;

class SCategoryLoop extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-loop-track]');
    this.list = this.querySelector('[data-loop-list]');
    if (!this.track || !this.list || this.list.children.length < 2) return;

    const originals = Array.from(this.list.children);
    // Two-row grid on smaller screens needs an even count per set so columns line up across sets.
    if (originals.length % 2 !== 0) {
      const filler = originals[0].cloneNode(true);
      filler.style.visibility = 'hidden';
      this.makeInert(filler);
      this.list.append(filler);
      originals.push(filler);
    }
    this.perSet = originals.length;
    for (let i = 1; i < SETS; i += 1) {
      for (const item of originals) {
        const clone = item.cloneNode(true);
        this.makeInert(clone);
        this.list.append(clone);
      }
    }

    this.onScroll = () => this.wrap();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.querySelector('[data-loop-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-loop-next]')?.addEventListener('click', () => this.step(1));
    this.resizeObserver = new ResizeObserver(() => this.center());
    this.resizeObserver.observe(this.track);
    requestAnimationFrame(() => this.center());
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  makeInert(node) {
    node.setAttribute('aria-hidden', 'true');
    node.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
  }

  setWidth() {
    return this.list.scrollWidth / SETS;
  }

  direction() {
    return getComputedStyle(this).direction === 'rtl' ? -1 : 1;
  }

  center() {
    const width = this.setWidth();
    if (!width) return;
    const offset = Math.abs(this.track.scrollLeft) % width;
    this.track.scrollLeft = (width * Math.floor(SETS / 2) + offset) * this.direction();
  }

  wrap() {
    const width = this.setWidth();
    if (!width) return;
    const pos = Math.abs(this.track.scrollLeft);
    if (pos < width || pos > width * (SETS - 2)) {
      this.track.scrollLeft = (width * Math.floor(SETS / 2) + (pos % width)) * this.direction();
    }
  }

  step(sign) {
    const amount = window.innerWidth <= 749 ? 200 : 350;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: amount * sign * this.direction(), behavior: smooth ? 'smooth' : 'auto' });
  }
}

if (!customElements.get('s-category-loop')) customElements.define('s-category-loop', SCategoryLoop);
